/**
 * An RFC on the account's Rules page, read and changed in the same place
 * (§5.27, Claude Design kit 67, R10 on piste A) — the app's sheet on the
 * account's components, in light only. There is no editing form any more:
 * « Edit details » turns the head into its fields where it stands, « Edit » a
 * statement; one block is open at a time, and « Keep in draft » at its foot
 * keeps that block alone in this browser's draft (`keepRfc`). While it differs,
 * the rest of the sheet waits and says why.
 *
 * The sheet reads the draft: a statement the draft changed says CHANGED, NEW,
 * REMOVED or MOVED OUT, with « Undo change »; « Remove statement » goes to the
 * draft at once; the refusals are said on their fields as how to fix them.
 */
import { useEffect, useMemo, useState } from 'react';
import type React from 'react';
import { RULE_STATUSES, type RuleRfc } from '@airlock/contracts/rules/document';
import { fieldsToFix, ruleProblemsByField } from '@airlock/contracts/rules/fields';
import { ruleFileName, splitRepoGlob } from '@airlock/contracts/rules/repository';
import { GhostButton, Input, PrimaryButton } from '../../../components/primitives';
import { color, font } from '../../../theme/tokens';
import { selectStyle, textareaStyle } from '../shared';
import { RuleField, StatementFields, globs, mono13 } from './rules-statement-fields';
import { folderName, moveSentence, sheetStatements, undoHead, undoStatement, type DraftChange, type Mark, type StatementMove } from './rules-draft';

type Statement = RuleRfc['statements'][number];
type Open = { kind: 'head' } | { kind: 'statement'; slug: string } | { kind: 'add' } | { kind: 'fresh' } | null;

export interface KeepItem {
  doc: RuleRfc;
  checks?: Record<string, string>;
  moved?: StatementMove[];
}

const STATUS_HINT: Record<string, string> = {
  draft: 'Written, applied nowhere.',
  approved: 'Applied.',
  enforced: 'Applied, and a broken MUST holds the merge.',
  superseded: 'Kept as history — another RFC replaces it.'
};
const HINT = 'Nothing applies before a pull request is merged.';

export function RulesSheet({
  origin,
  branch,
  change,
  current,
  fresh,
  folders,
  canWrite,
  q,
  matches,
  heldScript,
  onKeep,
  onUndoRfc,
  onUndoMove,
  onDelete,
  onFreshDone,
  onDirty,
  grip
}: {
  origin: string;
  /** The RFC as the branch holds it — null for one only the draft holds. */
  branch: RuleRfc | null;
  change: DraftChange | null;
  /** The RFC as the draft leaves it, or the new one being written. */
  current: RuleRfc;
  fresh: boolean;
  /** The folders RFCs are in, by the projects each names. */
  folders: readonly (readonly string[])[];
  canWrite: boolean;
  q: string;
  matches: (one: Statement) => boolean;
  heldScript: (path: string) => string | undefined;
  onKeep: (items: KeepItem[]) => void;
  onUndoRfc: () => void;
  onUndoMove: (slug: string) => void;
  onDelete: () => void;
  onFreshDone: (rfc: number | null) => void;
  /** What is being changed and not kept, or null. */
  onDirty: (what: string | null) => void;
  grip?: (slug: string) => React.ReactNode;
}): React.JSX.Element {
  const [open, setOpen] = useState<Open>(fresh ? { kind: 'fresh' } : null);
  const [head, setHead] = useState<RuleRfc | null>(fresh ? current : null);
  const [statement, setStatement] = useState<Statement | null>(null);
  const [written, setWritten] = useState<Record<string, string>>({});
  const [tried, setTried] = useState(false);
  const [left, setLeft] = useState<Set<string>>(new Set());
  const [refusal, setRefusal] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const begin = (next: Open): void => {
    setOpen(next);
    setTried(false);
    setLeft(new Set());
    setWritten({});
    setRefusal(null);
    setHead(next?.kind === 'head' || next?.kind === 'fresh' ? structuredClone(current) : null);
    setStatement(
      next?.kind === 'statement'
        ? structuredClone(current.statements.find((one) => one.slug === next.slug) ?? null)
        : next?.kind === 'add'
          ? { slug: '', level: 'MUST', text: '' }
          : null
    );
  };
  useEffect(() => {
    if (!flash) return;
    const timer = setTimeout(() => setFlash(null), 1600);
    return () => clearTimeout(timer);
  }, [flash]);

  const candidate = useMemo((): RuleRfc | null => {
    if (!open) return null;
    if ((open.kind === 'head' || open.kind === 'fresh') && head) return head;
    if (open.kind === 'statement' && statement)
      return { ...current, statements: current.statements.map((one) => (one.slug === open.slug ? statement : one)) };
    if (open.kind === 'add' && statement) return { ...current, statements: [...current.statements, statement] };
    return null;
  }, [open, head, statement, current]);
  const dirty = !!candidate && (open?.kind === 'fresh' || open?.kind === 'add' || JSON.stringify(candidate) !== JSON.stringify(current) || Object.keys(written).length > 0);
  const label = !open ? null : open.kind === 'statement' ? `${origin}:${current.rfc}/${open.slug}` : open.kind === 'head' ? `${origin}:${current.rfc} · details` : `${origin}:${current.rfc}`;
  useEffect(() => onDirty(dirty ? label : null), [dirty, label, onDirty]);
  useEffect(() => () => onDirty(null), [onDirty]);

  const problems = candidate ? ruleProblemsByField(candidate) : new Map<string, string>();
  const index = open?.kind === 'statement' ? (candidate?.statements.findIndex((one) => one === statement) ?? -1) : (candidate?.statements.length ?? 0) - 1;
  const own = [...problems.entries()].filter(([key]) =>
    open?.kind === 'head' ? key.startsWith('rfc.') : open?.kind === 'fresh' ? true : key.startsWith(`statement.${index}.`) || key === 'rfc.statements'
  );
  const said = (key: string): string | null => (tried || left.has(key) ? (problems.get(key) ?? null) : null);
  const leave = (key: string) => (): void => setLeft((was) => new Set(was).add(key));
  const lock = dirty ? `Keep or cancel ${label} first.` : null;

  const keepOpen = (): void => {
    setTried(true);
    if (!candidate || own.length) return;
    const named = new Set(candidate.statements.map((one) => one.check).filter(Boolean));
    const checks = Object.fromEntries(Object.entries(written).filter(([path]) => named.has(path)));
    onKeep([{ doc: candidate, ...(Object.keys(checks).length ? { checks } : {}) }]);
    setFlash(open?.kind === 'statement' || open?.kind === 'add' ? `s:${statement?.slug}` : 'head');
    if (open?.kind === 'fresh') onFreshDone(candidate.rfc);
    setOpen(null);
  };
  const cancel = (): void => {
    if (open?.kind === 'fresh') onFreshDone(null);
    setOpen(null);
  };
  const scripts = { text: (path: string): string | undefined => written[path] ?? heldScript(path), put: (path: string, text: string) => setWritten((was) => ({ ...was, [path]: text })) };
  const summary = own.filter(() => tried);
  const errorsNote =
    summary.length >= 2 ? (
      <div role="alert" style={{ padding: '9px 12px', background: color.badBg, boxShadow: `inset 2px 0 0 ${color.red}` }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: color.badText }}>{fieldsToFix(summary.length)}</div>
        {summary.map(([key, text]) => (
          <div key={key} style={{ fontSize: 12, color: color.badText, marginTop: 3, overflowWrap: 'anywhere' }}>
            {fieldName(key)} — {text}
          </div>
        ))}
      </div>
    ) : null;
  const refusalNote = refusal ? (
    <div role="alert" style={{ padding: '9px 12px', background: color.badBg, boxShadow: `inset 2px 0 0 ${color.red}`, fontSize: 12.5, color: color.badText }}>
      {refusal}
    </div>
  ) : null;

  const deleting = change?.kind === 'delete';
  const moved = change?.kind === 'put' ? (change.moved ?? []) : [];
  const entries = fresh ? [] : sheetStatements(branch, current, moved);
  const from = branch && folderName(branch.repos) !== folderName(current.repos) ? folderName(branch.repos) : null;
  // The head's own fields, compared one by one — the folder is said apart (MOVED).
  const headOf = (doc: RuleRfc): string => JSON.stringify([doc.title, doc.status, doc.summary, doc.scope?.paths ?? []]);
  const headChanged = branch ? headOf(branch) !== headOf(current) : false;
  const headMark: Mark | 'moved' | null = !branch ? (fresh ? null : 'new') : deleting ? 'removed' : headChanged ? 'changed' : from ? 'moved' : null;

  const statementForm = (value: Statement, onChange: (next: Statement) => void, at: number): React.JSX.Element => (
    <StatementFields origin={origin} rfc={current.rfc} rfcStatus={(head ?? current).status} value={value} onChange={onChange} said={(field) => said(`statement.${at}.${field}`)} onLeave={(field) => leave(`statement.${at}.${field}`)()} scripts={scripts} />
  );

  return (
    <article style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {(open?.kind === 'head' || open?.kind === 'fresh') && head ? (
        <EditFrame
          title={open.kind === 'fresh' ? `${origin}:${head.rfc || '…'} · new RFC` : `${origin}:${current.rfc} · editing`}
          foot={<Foot destroy={open.kind === 'head' && branch ? { label: 'Delete RFC', onClick: onDelete } : null} unchanged={!dirty} onCancel={cancel} onKeep={keepOpen} />}
        >
          {refusalNote}
          {errorsNote}
          {open.kind === 'fresh' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(90px, 120px) minmax(0, 260px)', gap: 12 }}>
              <RuleField label="Number" error={said('rfc.rfc')} hint="A number is kept — its rules are cited by it.">
                <Input value={String(head.rfc || '')} onBlur={leave('rfc.rfc')} onChange={(e) => setHead({ ...head, rfc: Number(e.target.value.replace(/\D/g, '')) || 0 })} style={mono13} />
              </RuleField>
              <RuleField label="Slug" error={said('rfc.slug')} hint="Fixed once kept — the file is named after it.">
                <Input value={head.slug} placeholder="the file’s name" onBlur={leave('rfc.slug')} onChange={(e) => setHead({ ...head, slug: e.target.value.trim() })} style={mono13} />
              </RuleField>
            </div>
          )}
          <RuleField label="Title" error={said('rfc.title')}>
            <Input value={head.title} placeholder="What the RFC is about" onBlur={leave('rfc.title')} onChange={(e) => setHead({ ...head, title: e.target.value })} />
          </RuleField>
          <RuleField label="Status" error={said('rfc.status')} hint={STATUS_HINT[head.status]}>
            <select value={head.status} onChange={(e) => setHead({ ...head, status: e.target.value as RuleRfc['status'] })} style={{ ...selectStyle, maxWidth: 260 }}>
              {RULE_STATUSES.map((one) => (
                <option key={one} value={one}>
                  {one}
                </option>
              ))}
            </select>
          </RuleField>
          <RuleField label="Summary" error={said('rfc.summary')}>
            <textarea value={head.summary} rows={3} placeholder="what the rules are for" onBlur={leave('rfc.summary')} onChange={(e) => setHead({ ...head, summary: e.target.value })} style={{ ...textareaStyle, fontFamily: 'inherit', fontSize: 13.5 }} />
          </RuleField>
          <RuleField
            label="Folder"
            error={said('rfc.repos')}
            hint={`Where it applies: at the top, every repository; in a folder, the projects it names. → ${ruleFileName(head.rfc, head.slug || '<slug>', head.repos ?? [])}`}
          >
            <FolderChoice value={head.repos ?? []} folders={folders} onChange={(repos) => setHead(withRepos(head, repos))} />
          </RuleField>
          {branch && folderName(branch.repos) !== folderName(head.repos) && head.slug ? (
            <p style={{ margin: 0, padding: '8px 10px', background: color.surfaceAlt, boxShadow: `inset 2px 0 0 ${color.accent}`, fontSize: 12.5, color: color.inkBody, overflowWrap: 'anywhere' }}>
              Moves {origin}:{head.rfc} from {folderName(branch.repos)} to {folderName(head.repos)} — the pull request removes {ruleFileName(head.rfc, head.slug, branch.repos ?? [])} and writes {ruleFileName(head.rfc, head.slug, head.repos ?? [])}. Its number, slug and statements stay.
            </p>
          ) : null}
          <RuleField label="Paths (optional)" error={said('rfc.scope')} hint="src/**, read from each project’s folder in a folder, from each repository’s top at the top. Left empty, every file.">
            <Input
              value={(head.scope?.paths ?? []).join(', ')}
              placeholder="globs, comma-separated"
              onChange={(e) => {
                const paths = globs(e.target.value);
                const { scope: _scope, ...rest } = head;
                setHead(paths ? { ...rest, scope: { paths } } : rest);
              }}
              style={mono13}
            />
          </RuleField>
          {open.kind === 'fresh' && (
            <>
              <div style={{ paddingTop: 10, borderTop: `1px solid ${color.line}`, fontFamily: font.mono, fontSize: 10.5, letterSpacing: '.12em', color: color.inkFaint }}>
                FIRST STATEMENT · {origin}:{head.rfc || '…'}/{head.statements[0]?.slug || '…'}
              </div>
              {statementForm(head.statements[0], (next) => setHead({ ...head, statements: [next, ...head.statements.slice(1)] }), 0)}
            </>
          )}
        </EditFrame>
      ) : (
        <header style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingBottom: 14, borderBottom: `1px solid ${color.line}` }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
            <span title="A number is kept — its rules are cited by it." style={{ fontFamily: font.mono, fontSize: 12, color: color.inkFaint }}>
              {origin}:{current.rfc} · {ruleFileName(current.rfc, current.slug, current.repos ?? [])}
              {from ? ` · moved from ${from}` : ''}
            </span>
            <Tag tone={current.status === 'enforced' ? 'accent' : 'plain'}>{current.status}</Tag>
            {headMark && <Tag tone="warn">{MARK[headMark]}</Tag>}
            <span style={{ flex: 1 }} />
            {flash === 'head' && <Kept />}
            {canWrite && headMark && (
              <GhostButton size="sm" disabled={!!lock} onClick={() => (!branch || deleting || headMark === 'new' ? onUndoRfc() : onKeep([{ doc: undoHead(branch, current) }]))}>
                Undo change
              </GhostButton>
            )}
            {canWrite && !deleting && (
              <GhostButton size="sm" disabled={!!lock} onClick={() => begin({ kind: 'head' })}>
                Edit details
              </GhostButton>
            )}
          </div>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: color.ink, overflowWrap: 'anywhere' }}>{current.title}</h2>
          <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.6, color: color.inkBody, overflowWrap: 'anywhere' }}>{current.summary}</p>
          <Facts items={[['FOLDER', current.repos?.length ? folderName(current.repos) : '/ · every repository'], ['PATHS', current.scope?.paths?.length ? current.scope.paths.map(pathLabel).join(' · ') : 'every file']]} />
          {lock && <span style={{ fontSize: 12, color: color.inkFaint }}>{lock}</span>}
          {deleting && <span style={{ fontSize: 12.5, color: color.inkFaint }}>Deleted in your draft — its rules keep applying until the pull request is merged.</span>}
        </header>
      )}

      {!fresh && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 0 8px' }}>
            <span style={{ fontFamily: font.mono, fontSize: 10.5, letterSpacing: '.12em', color: color.inkFaint }}>STATEMENTS</span>
            <span style={{ fontFamily: font.mono, fontSize: 11, color: color.inkFaint }}>{current.statements.length}</span>
            <span style={{ flex: 1 }} />
            {canWrite && !deleting && (
              <GhostButton size="sm" disabled={!!lock} onClick={() => begin({ kind: 'add' })}>
                Add a statement
              </GhostButton>
            )}
          </div>
          {entries.map((entry) => {
            const id = `${origin}:${current.rfc}/${entry.slug}`;
            const gone = entry.mark === 'removed' || entry.mark === 'moved-out';
            if (open?.kind === 'statement' && open.slug === entry.slug && statement && !gone) {
              return (
                <EditFrame
                  key={entry.slug}
                  title={`${id} · editing`}
                  foot={
                    <Foot
                      destroy={{
                        label: 'Remove statement',
                        onClick: () => {
                          const rest = current.statements.filter((one) => one.slug !== entry.slug);
                          if (!rest.length) return setRefusal(`${entry.slug} is the last statement of this RFC — delete the RFC instead.`);
                          onKeep([{ doc: { ...current, statements: rest } }]);
                          setOpen(null);
                        }
                      }}
                      unchanged={!dirty}
                      onCancel={cancel}
                      onKeep={keepOpen}
                    />
                  }
                >
                  {refusalNote}
                  {errorsNote}
                  {statementForm(statement, setStatement, index)}
                </EditFrame>
              );
            }
            const hit = matches(entry.one);
            return (
              <section key={entry.slug} style={{ position: 'relative', padding: '12px 0 14px', borderBottom: `1px solid ${color.hairSoft}`, display: 'flex', flexDirection: 'column', gap: 6, opacity: hit ? 1 : 0.45 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
                  {canWrite && !gone && !deleting && grip ? grip(entry.slug) : null}
                  <span style={{ fontFamily: font.mono, fontSize: 12, color: color.inkMuted, overflowWrap: 'anywhere' }}>{id}</span>
                  <Tag tone={entry.one.level.startsWith('MUST') ? 'bad' : entry.one.level.startsWith('SHOULD') ? 'warn' : 'plain'} mono>
                    {entry.one.level}
                  </Tag>
                  {entry.mark && <Tag tone="warn">{MARK[entry.mark]}</Tag>}
                  <span style={{ flex: 1 }} />
                  {flash === `s:${entry.slug}` && <Kept />}
                  {canWrite && entry.mark && entry.mark !== 'moved-in' && branch && (
                    <GhostButton size="sm" disabled={!!lock} onClick={() => (entry.mark === 'moved-out' ? onUndoMove(entry.slug) : onKeep([{ doc: undoStatement(branch, current, entry.slug) }]))}>
                      Undo change
                    </GhostButton>
                  )}
                  {canWrite && !gone && !deleting && (
                    <GhostButton size="sm" disabled={!!lock} onClick={() => begin({ kind: 'statement', slug: entry.slug })}>
                      Edit
                    </GhostButton>
                  )}
                </div>
                <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.6, color: gone ? color.inkFaint : color.ink, textDecoration: gone ? 'line-through' : 'none', overflowWrap: 'anywhere' }}>
                  <Lit text={entry.one.text} q={hit ? q : ''} />
                </p>
                <Facts
                  items={[
                    ...(entry.one.why ? ([['WHY', entry.one.why]] as [string, string][]) : []),
                    ...(entry.one.check ? ([['CHECK', `${entry.one.check} decides it in the session’s machine`]] as [string, string][]) : []),
                    ['PATHS', entry.one.paths?.length ? entry.one.paths.map(pathLabel).join(' · ') : 'as the RFC'],
                    ...(entry.one.status ? ([['STATUS', `${entry.one.status} — the RFC is ${current.status}`]] as [string, string][]) : []),
                    ...(entry.mark === 'moved-out' && entry.other ? ([['NOTE', moveSentence(origin, { slug: entry.slug, from: current.rfc, to: entry.other })]] as [string, string][]) : []),
                    ...(entry.mark === 'moved-in' && entry.other ? ([['FROM', `${origin}:${entry.other}`]] as [string, string][]) : [])
                  ]}
                />
              </section>
            );
          })}
          {open?.kind === 'add' && statement && (
            <EditFrame title={`${origin}:${current.rfc}/${statement.slug || '…'} · new statement`} foot={<Foot unchanged={false} onCancel={cancel} onKeep={keepOpen} />}>
              {errorsNote}
              {statementForm(statement, setStatement, index)}
            </EditFrame>
          )}
        </>
      )}
    </article>
  );
}

/** The folder of an RFC, chosen: the top, a folder RFCs are in, or a new one by the projects it names. */
function FolderChoice({ value, folders, onChange }: { value: readonly string[]; folders: readonly (readonly string[])[]; onChange: (repos: string[]) => void }): React.JSX.Element {
  const key = (list: readonly string[]): string => [...list].sort().join('+');
  const known = [...new Set(folders.filter((one) => one.length).map(key))].sort();
  const current = key(value);
  const [typing, setTyping] = useState(!!current && !known.includes(current));
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      <select
        value={typing ? '__new' : current}
        onChange={(e) => {
          if (e.target.value === '__new') return setTyping(true);
          setTyping(false);
          onChange(e.target.value ? e.target.value.split('+') : []);
        }}
        style={{ ...selectStyle, maxWidth: 260, fontFamily: font.mono }}
      >
        <option value="">/ · every repository</option>
        {known.map((one) => (
          <option key={one} value={one}>
            {one}/
          </option>
        ))}
        <option value="__new">New folder…</option>
      </select>
      {typing && (
        <Input
          value={value.join(', ')}
          placeholder="project folder names, comma-separated"
          onChange={(e) => onChange((globs(e.target.value) ?? []).map((one) => one.toLowerCase()))}
          style={{ ...mono13, maxWidth: 320 }}
        />
      )}
    </div>
  );
}

function EditFrame({ title, foot, children }: { title: string; foot: React.ReactNode; children: React.ReactNode }): React.JSX.Element {
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '14px 0 0 14px', boxShadow: `inset 2px 0 0 ${color.accent}`, borderBottom: `1px solid ${color.hairSoft}`, marginBottom: 2 }}>
      <span style={{ fontFamily: font.mono, fontSize: 12, color: color.ink }}>
        {title} <span style={{ fontFamily: 'inherit', color: color.inkFaint }}>· All fields are required unless marked optional.</span>
      </span>
      {children}
      {foot}
    </section>
  );
}

function Foot({ destroy, unchanged, onCancel, onKeep }: { destroy?: { label: string; onClick: () => void } | null; unchanged: boolean; onCancel: () => void; onKeep: () => void }): React.JSX.Element {
  return (
    <div style={{ position: 'sticky', bottom: 0, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 0', background: color.surface, borderTop: `1px solid ${color.line}` }}>
      {destroy && (
        <GhostButton size="sm" onClick={destroy.onClick}>
          {destroy.label}
        </GhostButton>
      )}
      <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: color.inkFaint }}>{HINT}</span>
      <GhostButton size="sm" onClick={onCancel}>
        Cancel
      </GhostButton>
      <PrimaryButton size="sm" tone="ink" disabled={unchanged} reason={unchanged ? 'Nothing to keep' : undefined} onClick={onKeep}>
        Keep in draft
      </PrimaryButton>
    </div>
  );
}

function Facts({ items }: { items: [string, string][] }): React.JSX.Element {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '64px minmax(0, 1fr)', gap: '2px 8px' }}>
      {items.map(([k, v]) => (
        <span key={k} style={{ display: 'contents' }}>
          <span style={{ fontFamily: font.mono, fontSize: 10.5, letterSpacing: '.08em', lineHeight: '18px', color: color.inkFaint }}>{k}</span>
          <span style={{ fontSize: 12.5, lineHeight: '18px', color: color.inkSubtle, overflowWrap: 'anywhere' }}>{v}</span>
        </span>
      ))}
    </div>
  );
}

function Kept(): React.JSX.Element {
  return (
    <span role="status" style={{ fontSize: 12.5, color: color.okText }}>
      ✓ Kept in draft
    </span>
  );
}

const MARK: Record<Mark | 'moved', string> = {
  new: 'NEW',
  changed: 'CHANGED',
  removed: 'REMOVED',
  'moved-out': 'MOVED OUT',
  'moved-in': 'MOVED IN',
  moved: 'MOVED'
};

function Tag({ tone, mono, children }: { tone: 'accent' | 'warn' | 'bad' | 'plain'; mono?: boolean; children: React.ReactNode }): React.JSX.Element {
  const [background, ink] =
    tone === 'accent' ? [color.okBg, color.okText] : tone === 'warn' ? [color.warnBg, color.warnText] : tone === 'bad' ? [color.badBg, color.badText] : [color.surfaceAlt, color.offText];
  return <span style={{ padding: '1px 7px', borderRadius: 999, background, color: ink, fontSize: 11, fontFamily: mono ? font.mono : 'inherit', whiteSpace: 'nowrap' }}>{children}</span>;
}

function Lit({ text, q }: { text: string; q: string }): React.JSX.Element {
  const needle = q.trim().toLowerCase();
  if (!needle) return <>{text}</>;
  const out: React.ReactNode[] = [];
  const lower = text.toLowerCase();
  let at = 0;
  for (let found = lower.indexOf(needle); found >= 0; found = lower.indexOf(needle, at)) {
    if (found > at) out.push(text.slice(at, found));
    out.push(
      <mark key={found} style={{ background: color.accentWash, color: 'inherit' }}>
        {text.slice(found, found + needle.length)}
      </mark>
    );
    at = found + needle.length;
  }
  out.push(text.slice(at));
  return <>{out}</>;
}

/** `web · src/client/**`: the project a glob names in a folder of several, then the glob. */
function pathLabel(glob: string): string {
  const { repo, glob: rest } = splitRepoGlob(glob);
  return repo ? `${repo} · ${rest}` : rest;
}

function withRepos(doc: RuleRfc, repos: string[]): RuleRfc {
  const { repos: _repos, ...rest } = doc;
  return repos.length ? { ...rest, repos: [...repos].sort() } : rest;
}

function fieldName(key: string): string {
  const field = key.split('.').pop() ?? key;
  const word = field === 'rfc' ? 'number' : field === 'repos' ? 'folder' : field === 'scope' ? 'paths' : field;
  return word.charAt(0).toUpperCase() + word.slice(1);
}

